import { EditorView } from "@tiptap/pm/view";
import { Editor } from "@tiptap/core";
import { PlainMessage } from "@bufbuild/protobuf";
import { UploadAttachment } from "protogen/common_pb";
import { processImages } from "./utils";
import { FileUpload, fromAttachment } from "components/creation/FileUploader";
import { markTables } from "./extensions/tableUtils";
import { generateRandomName } from "./utils";
import {
  GetUrlContentRequest,
  GetUrlContentResponse,
} from "protogen/files_service_pb";

export const PasteHandler = (
  _: EditorView,
  event: ClipboardEvent,
  {
    editor,
    onUpload,
    setAttachments,
    getContentRequest,
    updateFileUpload,
    inlineAttachmentsEnabled,
    externalAttachmentHandler,
  }: {
    editor: Editor | null;
    onUpload: (
      files: FileList | File[] | null,
      inlineReferences?: (string | null)[],
      callback?: (u: PlainMessage<UploadAttachment>[]) => void,
    ) => void;
    setAttachments: (attachments: PlainMessage<UploadAttachment>[]) => void;
    getContentRequest?: (
      payload?:
        | GetUrlContentRequest
        | PlainMessage<GetUrlContentRequest>
        | undefined,
    ) => Promise<GetUrlContentResponse | null>;
    updateFileUpload: (uploads: FileUpload[]) => void;
    inlineAttachmentsEnabled: boolean;
    externalAttachmentHandler?: (files: File[]) => void;
  },
) => {
  // Handle pasted images
  const files = Array.from(event.clipboardData?.files || []);
  // Handle copy and pasted html with images with our signed urls
  // as the source.  Since they expire, they cannot be copied over.

  const html = event.clipboardData?.getData("text/html");
  const plainText = event.clipboardData?.getData("text/plain");
  const isMSWord =
    html && html.includes("urn:schemas-microsoft-com:office:word");

  // MSWord will always include an image of the selected content. When there is content, this
  // is distracting since you have the text and an image. When there is no content, then this could
  // be a single image copied from MS Word.
  if (files.length > 0 && (!isMSWord || plainText === " ")) {
    // When clipboard has both files and HTML with image tags, prefer the HTML
    // path. Browsers convert pasted images (including animated GIFs) to static
    // PNGs in clipboardData.files, but the HTML retains the original image URL.
    const htmlHasImages =
      html &&
      !isMSWord &&
      (() => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        return doc.querySelectorAll("img[src]").length > 0;
      })();

    if (!htmlHasImages) {
      // Editor paste handler is synchronous
      (async (editor: Editor | null) => {
        processImages(
          files,
          editor,
          onUpload,
          setAttachments,
          inlineAttachmentsEnabled,
          externalAttachmentHandler,
        );
      })(editor);
      return true;
    }
    // Fall through to HTML handling which preserves original image format
  }

  if (html && !isMSWord) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const docUpdated = markTables(doc);
    const imgs = doc.querySelectorAll("img");
    const fetchPromises: Promise<FileUpload | undefined>[] = [];
    if (imgs.length) {
      // Collect all base64 images first, then process them together
      const base64Files: File[] = [];

      // We use this pattern to avoid typescript warnings when looping over a NodeList
      Array.from(imgs).forEach((img) => {
        const title = generateRandomName("pasted-");
        img.title = title;
        // tiptap will set the height.  We want to keep the aspect ratio while allowing
        // our resizable extension to work (it changes width and maintains aspect ratio)
        img.setAttribute("height", "auto");
        const imgSrc = img.src;
        // Pasted html can have two types of src images, the base64 content or a url.
        if (imgSrc.startsWith("data:")) {
          // Handle the base64 case
          const mimeMatch = imgSrc.match(/^data:([^;,]+)/);
          const mimeType = mimeMatch?.[1] || "image/jpeg";
          const base64Data = imgSrc.split(",")[1];
          const binaryString = window.atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: mimeType });
          const file = new File([blob], title, { type: mimeType });
          base64Files.push(file);
        } else {
          // Handle the URL case
          const fetchPromise = getContentRequest?.({
            name: title,
            url: img.src,
          }).then((response) => {
            const attachment = response?.attachment;
            if (attachment) {
              const upload = fromAttachment(attachment);
              upload.validationKey = response.validationKey;
              upload.s3Key = response.s3Key;
              upload.inlineReference = title;
              return upload;
            }
          });
          if (fetchPromise) {
            fetchPromises.push(fetchPromise);
          }
        }
      });

      // Process all base64 images at once
      if (base64Files.length > 0) {
        processImages(
          base64Files,
          editor,
          onUpload,
          setAttachments,
          inlineAttachmentsEnabled,
          externalAttachmentHandler,
        );
      }
      if (fetchPromises.length > 0) {
        Promise.all(fetchPromises).then((uploads) => {
          const validUploads = uploads.filter(
            // @ts-ignore
            (u): u is FileUpload => u !== undefined,
          );
          // @ts-ignore
          updateFileUpload([...validUploads]);

          editor?.commands.insertContent(doc.body.innerHTML);
          return true;
        });
      }
      return true;
    }
    if (docUpdated) {
      editor?.commands.insertContent(doc.body.innerHTML);
      return true;
    }
  }

  // Handle Edge's "paste as link" - check for text/uri-list format
  const uriList = event.clipboardData?.getData("text/uri-list");
  if (uriList) {
    // text/uri-list format is newline-separated URLs, take the first one
    const url = uriList.split("\n")[0].trim();
    if (url && url.length > 0) {
      try {
        const parsedUrl = new URL(url);
        // Only accept http/https URLs, reject other URI schemes
        if (parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:") {
          editor?.commands.insertContent(url);
          return true;
        }
      } catch {
        // Invalid URL
      }
      // Not a valid http/https URL - iOS Safari bug puts URL-encoded text here
      // Explicitly use plain text instead
      event.preventDefault();
      const plainText = event.clipboardData?.getData("text/plain");
      if (plainText && editor) {
        editor.commands.insertContent(plainText);
        return true;
      }
    }
  }

  return false;
};
